﻿/* ============================================================
   Klausurphase Flashcards — modernes, ästhetisches Design
   ============================================================ */
:root {
  --bg: #f6f7fb;
  --bg-elev: #ffffff;
  --bg-soft: #f1f3f9;
  --text: #0f172a;
  --text-muted: #64748b;
  --border: #e5e7eb;
  --border-soft: #eef0f4;
  --accent: #6366f1;
  --accent-strong: #4f46e5;
  --accent-soft: #eef2ff;
  --success: #10b981;
  --warn: #f59e0b;
  --danger: #ef4444;
  --pink: #ec4899;
  --shadow-sm: 0 1px 2px rgba(15, 23, 42, .04);
  --shadow: 0 6px 18px rgba(15, 23, 42, .06);
  --shadow-lg: 0 24px 48px -16px rgba(15, 23, 42, .18);
  --radius: 14px;
  --radius-lg: 22px;
  --transition: 200ms cubic-bezier(.4,.2,.2,1);
}

[data-theme="dark"] {
  --bg: #0b1020;
  --bg-elev: #131a2c;
  --bg-soft: #1a2238;
  --text: #e6e8ee;
  --text-muted: #8b95ad;
  --border: #232c43;
  --border-soft: #1d263c;
  --accent: #818cf8;
  --accent-strong: #6366f1;
  --accent-soft: #1e1f3a;
  --shadow-sm: 0 1px 2px rgba(0,0,0,.3);
  --shadow: 0 10px 30px rgba(0,0,0,.35);
  --shadow-lg: 0 30px 60px -16px rgba(0,0,0,.5);
}

* { box-sizing: border-box; }
html, body { margin: 0; padding: 0; height: 100%; }
body {
  font-family: 'Inter', system-ui, -apple-system, Segoe UI, Roboto, sans-serif;
  background: var(--bg);
  color: var(--text);
  font-size: 15px;
  line-height: 1.55;
  -webkit-font-smoothing: antialiased;
}

#app {
  display: grid;
  grid-template-columns: 260px 1fr;
  min-height: 100vh;
}

/* ===================== SIDEBAR ===================== */
.sidebar {
  background: var(--bg-elev);
  border-right: 1px solid var(--border);
  padding: 22px 18px;
  display: flex;
  flex-direction: column;
  gap: 18px;
  position: sticky;
  top: 0;
  height: 100vh;
}
.brand {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 4px 6px 18px 6px;
  border-bottom: 1px solid var(--border-soft);
}
.brand-logo {
  width: 42px; height: 42px;
  display: grid; place-items: center;
  background: linear-gradient(135deg, var(--accent), var(--pink));
  border-radius: 12px;
  font-size: 22px;
  box-shadow: var(--shadow);
}
.brand-title { font-weight: 700; font-size: 15px; }
.brand-sub { font-size: 12px; color: var(--text-muted); }

.nav { display: flex; flex-direction: column; gap: 4px; }
.nav-item {
  display: flex; align-items: center; gap: 12px;
  padding: 10px 12px;
  background: transparent;
  border: 0;
  border-radius: 10px;
  color: var(--text-muted);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  text-align: left;
  width: 100%;
  font-family: inherit;
  transition: background var(--transition), color var(--transition);
}
.nav-item span { font-size: 16px; }
.nav-item:hover { background: var(--bg-soft); color: var(--text); }
.nav-item.active {
  background: var(--accent-soft);
  color: var(--accent-strong);
  font-weight: 600;
}

.streak-box {
  margin-top: auto;
  padding: 16px;
  background: linear-gradient(135deg, rgba(99,102,241,.1), rgba(236,72,153,.08));
  border: 1px solid var(--border-soft);
  border-radius: 14px;
}
.streak-label { font-size: 12px; color: var(--text-muted); }
.streak-value { font-size: 28px; font-weight: 700; }
.streak-sub { font-size: 12px; color: var(--text-muted); margin-top: 4px; }

.theme-toggle button {
  width: 100%;
  padding: 10px;
  background: var(--bg-soft);
  border: 1px solid var(--border-soft);
  border-radius: 10px;
  cursor: pointer;
  color: var(--text);
  font-size: 16px;
}

/* ===================== MAIN ===================== */
.main {
  padding: 32px 40px 60px;
  max-width: 1280px;
  width: 100%;
  margin: 0 auto;
}
.view { display: none; animation: fadeIn 220ms ease; }
.view.active { display: block; }
@keyframes fadeIn {
  from { opacity: 0; transform: translateY(4px); }
  to { opacity: 1; transform: translateY(0); }
}

.page-head {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 28px;
}
.page-head h1 { margin: 0; font-size: 28px; font-weight: 700; letter-spacing: -.01em; }
.muted { color: var(--text-muted); margin: 4px 0 0 0; font-size: 14px; }
.head-actions { display: flex; gap: 10px; align-items: center; }

/* ===================== BUTTONS / INPUTS ===================== */
.btn {
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  padding: 10px 16px;
  border-radius: 10px;
  border: 1px solid transparent;
  cursor: pointer;
  transition: transform var(--transition), background var(--transition), box-shadow var(--transition);
}
.btn:active { transform: scale(.97); }
.btn-primary {
  background: linear-gradient(135deg, var(--accent), var(--accent-strong));
  color: #fff;
  box-shadow: 0 6px 16px -4px rgba(99,102,241,.45);
}
.btn-primary:hover { box-shadow: 0 10px 22px -6px rgba(99,102,241,.55); }
.btn-secondary {
  background: var(--bg-soft);
  color: var(--text);
  border-color: var(--border);
}
.btn-secondary:hover { background: var(--border-soft); }
.btn-danger { background: var(--danger); color: #fff; }
.btn-ghost {
  background: transparent;
  border: 0;
  color: var(--text-muted);
  cursor: pointer;
  padding: 6px 8px;
  border-radius: 8px;
  font-size: 16px;
}
.btn-ghost:hover { background: var(--bg-soft); }

.select, .input {
  font-family: inherit;
  font-size: 14px;
  padding: 9px 12px;
  background: var(--bg-elev);
  color: var(--text);
  border: 1px solid var(--border);
  border-radius: 10px;
  outline: none;
  transition: border var(--transition);
}
.select:focus, .input:focus { border-color: var(--accent); }
.input.small { max-width: 120px; }

/* ===================== STATS ===================== */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 14px;
  margin-bottom: 24px;
}
.stat-card {
  background: var(--bg-elev);
  border: 1px solid var(--border-soft);
  border-radius: var(--radius);
  padding: 18px;
  box-shadow: var(--shadow-sm);
  transition: transform var(--transition), box-shadow var(--transition);
}
.stat-card:hover { transform: translateY(-2px); box-shadow: var(--shadow); }
.stat-card-highlight {
  border-color: var(--accent);
  background: linear-gradient(135deg, var(--bg-elev), rgba(99,102,241,.08));
  box-shadow: 0 4px 14px rgba(99,102,241,.12);
}
.stat-card-highlight .stat-value { color: var(--accent-strong); }
.stat-icon { font-size: 20px; margin-bottom: 6px; }
.stat-label { font-size: 12px; color: var(--text-muted); text-transform: uppercase; letter-spacing: .04em; }
.stat-value { font-size: 30px; font-weight: 700; margin: 4px 0; letter-spacing: -.02em; }
.stat-sub { font-size: 12px; color: var(--text-muted); }

/* ===================== PANELS ===================== */
.grid-2 {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 16px;
}
@media (max-width: 980px) { .grid-2 { grid-template-columns: 1fr; } }
.panel {
  background: var(--bg-elev);
  border: 1px solid var(--border-soft);
  border-radius: var(--radius);
  padding: 22px;
  box-shadow: var(--shadow-sm);
  margin-bottom: 16px;
}
.panel.danger { border-color: rgba(239,68,68,.25); }
.panel-head {
  display: flex; align-items: center; justify-content: space-between;
  margin-bottom: 16px;
}
.panel-head h2 { margin: 0; font-size: 16px; font-weight: 700; }

/* ===================== DECK LIST ===================== */
.deck-list { display: flex; flex-direction: column; gap: 10px; }
.deck-card {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px;
  background: var(--bg-soft);
  border: 1px solid transparent;
  border-radius: 12px;
  cursor: pointer;
  transition: border var(--transition), background var(--transition);
}
.deck-card:hover { border-color: var(--accent); }
.deck-icon {
  width: 44px; height: 44px;
  border-radius: 12px;
  display: grid; place-items: center;
  font-size: 22px;
  background: var(--bg-elev);
  border: 1px solid var(--border-soft);
}
.deck-meta { flex: 1; min-width: 0; }
.deck-name { font-weight: 600; font-size: 14px; }
.deck-sub { font-size: 12px; color: var(--text-muted); margin-top: 2px; }
.deck-progress {
  width: 100%;
  height: 6px;
  background: var(--border-soft);
  border-radius: 99px;
  overflow: hidden;
  margin-top: 8px;
}
.deck-progress-fill {
  height: 100%;
  background: linear-gradient(90deg, var(--accent), var(--pink));
  transition: width var(--transition);
}
.deck-action {
  background: var(--bg-elev);
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 6px 10px;
  font-size: 12px;
  color: var(--text-muted);
  font-weight: 600;
}
.deck-action.has-due { background: var(--accent); color: white; border-color: transparent; }

/* ===================== DECK PILL TABS (Karten / Analyse) ===================== */
.deck-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 18px;
}
.deck-tab {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 9px 14px;
  background: var(--bg-elev);
  border: 1px solid var(--border);
  border-radius: 999px;
  cursor: pointer;
  color: var(--text);
  font: inherit;
  font-size: 13px;
  font-weight: 600;
  line-height: 1;
  transition: background var(--transition), border-color var(--transition),
              color var(--transition), transform var(--transition);
}
.deck-tab:hover {
  border-color: var(--accent);
  transform: translateY(-1px);
}
.deck-tab.active {
  background: var(--accent);
  color: #fff;
  border-color: transparent;
}
.deck-tab-icon { font-size: 15px; line-height: 1; }
.deck-tab-name { white-space: nowrap; }
.deck-tab-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  padding: 2px 7px;
  border-radius: 999px;
  background: var(--bg-soft);
  color: var(--text-muted);
  font-size: 11px;
  font-weight: 700;
}
.deck-tab.active .deck-tab-count {
  background: rgba(255, 255, 255, 0.22);
  color: #fff;
}

/* ===================== ANALYTICS – Übersicht "Alle Fächer" ===================== */
.analytics-overview { display: flex; flex-direction: column; gap: 18px; }
.overview-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 14px;
}
.overview-card {
  background: var(--bg-soft);
  border: 1px solid var(--border-soft);
  border-radius: 14px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  transition: border-color var(--transition), transform var(--transition);
}
.overview-card:hover { border-color: var(--accent); }
.overview-head { display: flex; align-items: center; gap: 12px; }
.overview-icon {
  width: 40px; height: 40px;
  border-radius: 10px;
  display: grid; place-items: center;
  font-size: 20px;
  background: var(--bg-elev);
  border: 1px solid var(--border-soft);
}
.overview-title { flex: 1; min-width: 0; }
.overview-name {
  font-weight: 700;
  font-size: 14px;
  line-height: 1.2;
}
.overview-sub { font-size: 12px; margin-top: 2px; }
.overview-progress { display: flex; flex-direction: column; gap: 4px; }
.overview-progress-bar {
  height: 6px;
  background: var(--border-soft);
  border-radius: 99px;
  overflow: hidden;
}
.overview-progress-fill {
  height: 100%;
  background: linear-gradient(90deg, var(--accent), var(--pink));
  transition: width var(--transition);
}
.overview-progress-label { font-size: 11px; }
.overview-metrics {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
}
.overview-metric {
  background: var(--bg-elev);
  border: 1px solid var(--border-soft);
  border-radius: 8px;
  padding: 8px 10px;
  text-align: center;
}
.overview-metric-label {
  font-size: 10.5px;
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: .04em;
}
.overview-metric-value {
  font-size: 15px;
  font-weight: 700;
  color: var(--text);
  margin-top: 2px;
}
.overview-maturity {
  display: flex;
  height: 8px;
  border-radius: 99px;
  overflow: hidden;
  background: var(--border-soft);
}
.overview-mat-seg { display: block; height: 100%; }
.overview-cta { align-self: flex-start; }
@media (max-width: 560px) {
  .overview-metrics { grid-template-columns: repeat(2, 1fr); }
}

/* Deck-Card Metrik-Chips (Dashboard) – kompakt + farbig */
.deck-card .overview-metrics {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
}
.deck-card .overview-metrics > span {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 8px;
  border-radius: 999px;
  font-size: 11.5px;
  font-weight: 500;
  background: var(--bg-elev);
  border: 1px solid var(--border-soft);
  color: var(--text-muted);
  white-space: nowrap;
}
.deck-card .overview-metrics > span.metric-due {
  background: rgba(99,102,241,.10);
  border-color: rgba(99,102,241,.35);
  color: var(--accent-strong);
  font-weight: 600;
}
.deck-card .overview-metrics > span.metric-learning {
  background: rgba(245,158,11,.10);
  border-color: rgba(245,158,11,.35);
  color: #b45309;
  font-weight: 600;
}
.deck-card .overview-metrics > span.metric-new {
  background: rgba(236,72,153,.10);
  border-color: rgba(236,72,153,.35);
  color: #be185d;
  font-weight: 600;
}

/* ===================== ANALYTICS – Zeitplan-Chart ===================== */
.schedule-panel { margin-top: 18px; }
.schedule-head { display: flex; justify-content: space-between; align-items: flex-end; gap: 12px; margin-bottom: 14px; flex-wrap: wrap; }
.schedule-head h2 { margin: 0 0 4px; font-size: 16px; font-weight: 700; }
.schedule-head .muted { margin: 0; font-size: 12px; }
.schedule-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 14px;
}
.schedule-card {
  background: var(--bg-soft);
  border: 1px solid var(--border-soft);
  border-radius: 14px;
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.schedule-card-head { display: flex; justify-content: space-between; align-items: center; gap: 10px; flex-wrap: wrap; }
.schedule-card-head h3 { margin: 0; font-size: 14px; font-weight: 700; }
.schedule-status {
  font-size: 11px;
  font-weight: 600;
  padding: 3px 8px;
  border-radius: 99px;
  background: var(--bg-elev);
  border: 1px solid var(--border-soft);
  color: var(--text-muted);
}
.schedule-status.good { color: var(--success); border-color: rgba(16, 185, 129, .35); background: rgba(16, 185, 129, .08); }
.schedule-status.bad { color: var(--danger); border-color: rgba(239, 68, 68, .35); background: rgba(239, 68, 68, .08); }
.schedule-chart { width: 100%; height: auto; display: block; }
.schedule-chart .schedule-axis { stroke: var(--border); stroke-width: 1; }
.schedule-chart .schedule-ideal { fill: none; stroke: var(--text-muted); stroke-width: 1.25; stroke-dasharray: 4 4; opacity: .7; }
.schedule-chart .schedule-actual { fill: none; stroke: var(--accent); stroke-width: 2; stroke-linejoin: round; stroke-linecap: round; }
.schedule-chart .schedule-area { fill: var(--accent); opacity: .12; }
.schedule-chart .schedule-today { stroke: var(--pink); stroke-width: 1; stroke-dasharray: 2 3; opacity: .8; }
.schedule-meta {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 8px;
  font-size: 11px;
  color: var(--text-muted);
}

/* ===================== HEATMAP ===================== */
.heatmap {
  display: grid;
  grid-template-columns: repeat(13, 1fr);
  gap: 4px;
  margin-bottom: 12px;
}
.hm-cell {
  width: 100%;
  aspect-ratio: 1;
  border-radius: 4px;
  background: var(--border-soft);
  display: inline-block;
}
.hm-cell[data-l="1"] { background: rgba(99,102,241,.25); }
.hm-cell[data-l="2"] { background: rgba(99,102,241,.5); }
.hm-cell[data-l="3"] { background: rgba(99,102,241,.75); }
.hm-cell[data-l="4"] { background: var(--accent-strong); }
.heatmap-legend {
  display: flex; align-items: center; gap: 6px; font-size: 12px;
}
.heatmap-legend .hm-cell { width: 12px; height: 12px; aspect-ratio: auto; }

/* ===================== REVIEW ===================== */
.review-progress { margin-bottom: 18px; }
.rp-bar {
  height: 6px; background: var(--border-soft);
  border-radius: 99px; overflow: hidden;
}
.rp-fill {
  height: 100%; width: 0;
  background: linear-gradient(90deg, var(--accent), var(--pink));
  transition: width 400ms ease;
}
.rp-labels {
  display: flex; gap: 16px; align-items: center;
  margin-top: 8px;
  font-size: 13px; color: var(--text-muted);
}
.dot {
  display: inline-block; width: 8px; height: 8px;
  border-radius: 50%; margin-right: 6px;
  vertical-align: middle;
}
.dot-new { background: #94a3b8; }
.dot-learn { background: #fbbf24; }
.dot-due { background: var(--accent); }

.card-stage {
  min-height: 560px;
  display: grid; place-items: start center;
  margin-bottom: 22px;
}

.empty {
  text-align: center;
  color: var(--text-muted);
}
.empty-icon { font-size: 64px; margin-bottom: 12px; }
.empty h2 { color: var(--text); }

.flashcard {
  width: 100%;
  max-width: 960px;
  perspective: 1500px;
}
.hidden { display: none !important; }
.card-meta {
  display: flex; gap: 8px;
  margin-bottom: 12px;
  flex-wrap: wrap;
}
.chip {
  font-size: 12px;
  padding: 4px 10px;
  background: var(--bg-soft);
  color: var(--text-muted);
  border-radius: 99px;
  border: 1px solid var(--border-soft);
  font-weight: 500;
}
.chip-state { background: var(--accent-soft); color: var(--accent-strong); }
.chip-id { font-family: 'JetBrains Mono', monospace; }

.card-inner {
  position: relative;
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: auto;
  min-height: 480px;
  transform-style: preserve-3d;
  transition: transform 600ms cubic-bezier(.34,1.4,.64,1);
}
.card-inner > .card-face {
  grid-area: 1 / 1 / 2 / 2;
}
.card-inner.flipped { transform: rotateY(180deg); }
.card-face {
  background: var(--bg-elev);
  border: 1px solid var(--border-soft);
  border-radius: var(--radius-lg);
  padding: 36px 44px;
  box-shadow: var(--shadow-lg);
  backface-visibility: hidden;
  display: flex;
  flex-direction: column;
}
.card-face.card-back {
  transform: rotateY(180deg);
  background: linear-gradient(135deg, var(--bg-elev) 0%, var(--accent-soft) 100%);
}
.face-label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: .12em;
  text-transform: uppercase;
  color: var(--text-muted);
  margin-bottom: 14px;
}
.face-content {
  flex: 1;
  font-size: 19px;
  line-height: 1.65;
  overflow: visible;
  white-space: pre-wrap;
}
.face-content code {
  font-family: 'JetBrains Mono', monospace;
  background: var(--bg-soft);
  padding: 2px 6px;
  border-radius: 4px;
  font-size: .9em;
}
.face-content strong { font-weight: 700; color: var(--accent-strong); }

.card-fig-hint {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 14px;
  padding: 6px 12px;
  background: var(--accent-soft);
  color: var(--accent-strong);
  border-radius: 99px;
  font-size: 12px;
  font-weight: 600;
}
.card-figure {
  margin: 16px 0 0 0;
  text-align: center;
}
.card-figure img {
  max-width: 100%;
  max-height: 60vh;
  border-radius: 12px;
  border: 1px solid var(--border-soft);
  background: #fff;
  box-shadow: 0 6px 18px -10px rgba(15, 23, 42, .35);
  cursor: zoom-in;
  transition: transform var(--transition);
}
.card-figure img:hover { transform: scale(1.01); }

.card-source {
  margin-top: 12px;
  text-align: center;
  font-size: 13px;
}
.card-source a {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  background: var(--bg-soft);
  color: var(--accent-strong);
  border: 1px solid var(--border-soft);
  border-radius: 99px;
  text-decoration: none;
  font-weight: 600;
  transition: background var(--transition), border var(--transition);
}
.card-source a:hover {
  background: var(--accent-soft);
  border-color: var(--accent);
}

/* ===================== LIGHTBOX ===================== */
.lightbox {
  position: fixed;
  inset: 0;
  background: rgba(8, 10, 25, .85);
  display: none;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 24px;
  cursor: zoom-out;
  backdrop-filter: blur(4px);
}
.lightbox.show { display: flex; animation: fadeIn 200ms ease; }
.lightbox img {
  max-width: 95vw;
  max-height: 95vh;
  border-radius: 12px;
  box-shadow: 0 30px 80px rgba(0,0,0,.6);
}
.btn-flip {
  margin-top: 18px;
  align-self: center;
  background: var(--accent);
  color: white;
  border: 0;
  padding: 12px 22px;
  border-radius: 12px;
  cursor: pointer;
  font-family: inherit;
  font-weight: 600;
  font-size: 14px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  box-shadow: 0 8px 18px -6px rgba(99,102,241,.5);
}
.btn-flip kbd {
  font-family: 'JetBrains Mono', monospace;
  font-size: 11px;
  padding: 2px 6px;
  background: rgba(255,255,255,.18);
  border-radius: 4px;
}

.rating {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 10px;
  max-width: 960px;
  margin: 0 auto;
}
.rate-btn {
  display: flex; align-items: center; gap: 12px;
  padding: 14px 16px;
  border: 1px solid var(--border);
  background: var(--bg-elev);
  border-radius: 12px;
  cursor: pointer;
  font-family: inherit;
  text-align: left;
  transition: transform var(--transition), border var(--transition), box-shadow var(--transition);
  color: var(--text);
}
.rate-btn:hover { transform: translateY(-2px); box-shadow: var(--shadow); }
.rate-btn .rk {
  width: 26px; height: 26px;
  border-radius: 8px;
  background: var(--bg-soft);
  display: grid; place-items: center;
  font-family: 'JetBrains Mono', monospace;
  font-size: 12px;
  font-weight: 700;
  color: var(--text-muted);
}
.rate-btn .rl { font-weight: 700; font-size: 14px; }
.rate-btn .ri { font-size: 12px; color: var(--text-muted); margin-top: 2px; }
.rate-again { border-color: rgba(239,68,68,.3); }
.rate-again:hover { border-color: var(--danger); }
.rate-hard { border-color: rgba(245,158,11,.3); }
.rate-hard:hover { border-color: var(--warn); }
.rate-good { border-color: rgba(16,185,129,.3); }
.rate-good:hover { border-color: var(--success); }
.rate-easy { border-color: rgba(99,102,241,.3); }
.rate-easy:hover { border-color: var(--accent); }

/* ===================== BROWSE ===================== */
.browse-list {
  display: grid;
  gap: 10px;
}
.browse-row {
  background: var(--bg-elev);
  border: 1px solid var(--border-soft);
  border-radius: 12px;
  padding: 14px 18px;
  display: grid;
  grid-template-columns: 60px 1fr auto;
  gap: 14px;
  align-items: center;
}
.browse-id { font-family: 'JetBrains Mono', monospace; color: var(--text-muted); font-size: 13px; }
.browse-q { font-weight: 600; }
.browse-a { font-size: 13px; color: var(--text-muted); margin-top: 4px; }
.browse-meta { font-size: 11px; color: var(--text-muted); text-align: right; min-width: 110px; }

/* ===================== ANALYTICS ===================== */
.chap-list { display: flex; flex-direction: column; gap: 10px; max-height: 360px; overflow-y: auto; }
.chap-row {
  display: grid;
  grid-template-columns: 1fr 60px 60px;
  gap: 12px;
  align-items: center;
  padding: 10px 12px;
  background: var(--bg-soft);
  border-radius: 10px;
}
.chap-name { font-size: 13px; font-weight: 500; }
.chap-bar { height: 6px; background: var(--border-soft); border-radius: 99px; overflow: hidden; margin-top: 6px; }
.chap-bar-fill { height: 100%; background: var(--success); }
.chap-bar-fill.warn { background: var(--warn); }
.chap-bar-fill.bad { background: var(--danger); }
.chap-pct { text-align: right; font-family: 'JetBrains Mono', monospace; font-size: 13px; font-weight: 600; }
.chap-cnt { text-align: right; font-size: 12px; color: var(--text-muted); }

.legend { display: flex; gap: 14px; flex-wrap: wrap; margin-top: 12px; font-size: 12px; color: var(--text-muted); }
.legend i {
  width: 10px; height: 10px;
  display: inline-block;
  border-radius: 3px;
  margin-right: 4px;
  vertical-align: middle;
}
.hardest-list { display: flex; flex-direction: column; gap: 10px; }
.hardest-row {
  display: grid;
  grid-template-columns: 1fr auto auto;
  gap: 14px;
  align-items: center;
  padding: 12px 16px;
  background: var(--bg-soft);
  border-radius: 10px;
}
.hardest-q { font-weight: 500; font-size: 14px; }
.hardest-meta { font-size: 12px; color: var(--text-muted); }

/* ===================== SETTINGS ===================== */
.exam-grid { display: grid; gap: 12px; }
.exam-row {
  display: grid;
  grid-template-columns: 200px 1fr 140px 200px;
  gap: 12px;
  align-items: center;
  padding: 14px;
  background: var(--bg-soft);
  border-radius: 10px;
}
.exam-name { font-weight: 600; }
.exam-sub { font-size: 12px; color: var(--text-muted); }
.exam-target {
  font-family: 'JetBrains Mono', monospace;
  font-size: 13px;
  font-weight: 600;
  text-align: right;
}
.setting-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 14px 0;
  border-bottom: 1px solid var(--border-soft);
}
.setting-row:last-child { border-bottom: 0; }
.set-title { font-weight: 600; font-size: 14px; }
.set-sub { font-size: 12px; }

input[type="checkbox"] {
  width: 36px; height: 20px;
  appearance: none;
  background: var(--border);
  border-radius: 99px;
  position: relative;
  cursor: pointer;
  transition: background var(--transition);
}
input[type="checkbox"]:checked { background: var(--accent); }
input[type="checkbox"]::before {
  content: '';
  position: absolute;
  top: 2px; left: 2px;
  width: 16px; height: 16px;
  background: white;
  border-radius: 50%;
  transition: transform var(--transition);
}
input[type="checkbox"]:checked::before { transform: translateX(16px); }

input[type="date"], input[type="number"], input[type="file"] {
  font-family: inherit;
  padding: 8px 12px;
  background: var(--bg-elev);
  color: var(--text);
  border: 1px solid var(--border);
  border-radius: 8px;
}

/* ===================== TOAST ===================== */
.toast {
  position: fixed;
  bottom: 24px; left: 50%;
  transform: translateX(-50%) translateY(100px);
  background: var(--text);
  color: var(--bg-elev);
  padding: 10px 18px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 500;
  box-shadow: var(--shadow-lg);
  opacity: 0;
  transition: all 280ms cubic-bezier(.34,1.4,.64,1);
  z-index: 100;
  pointer-events: none;
}
.toast.show {
  transform: translateX(-50%) translateY(0);
  opacity: 1;
}

/* ===================== KI-TUTOR CHAT ===================== */
.chat-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(8, 10, 25, .45);
  backdrop-filter: blur(2px);
  opacity: 0;
  pointer-events: none;
  transition: opacity .2s;
  z-index: 75;
}
.chat-backdrop.open {
  opacity: 1;
  pointer-events: auto;
}
.chat-panel {
  position: fixed;
  top: 0; right: 0;
  width: 440px;
  max-width: 92vw;
  height: 100vh;
  background: var(--bg-elev);
  border-left: 1px solid var(--border-soft);
  box-shadow: -10px 0 30px -15px rgba(15,23,42,.25);
  display: flex;
  flex-direction: column;
  transform: translateX(100%);
  transition: transform 320ms cubic-bezier(.4,.2,.2,1);
  z-index: 260;
}
.chat-panel.open { transform: translateX(0); }
body.chat-open .main { padding-right: calc(440px + 32px); }
@media (max-width: 1100px) {
  body.chat-open .main { padding-right: 32px; }
}

.chat-head {
  display: flex; align-items: center; justify-content: space-between;
  gap: 12px;
  padding: 18px 20px;
  border-bottom: 1px solid var(--border-soft);
}
.chat-head-title { display: flex; align-items: center; gap: 12px; min-width: 0; }
.chat-ico {
  width: 38px; height: 38px;
  display: grid; place-items: center;
  background: linear-gradient(135deg, var(--accent), var(--accent-strong));
  color: #fff; border-radius: 12px; font-size: 18px;
  flex-shrink: 0;
}
.chat-h { font-weight: 700; font-size: 15px; }
.chat-sub { font-size: 12px; color: var(--text-muted, #64748b); }
.chat-close {
  background: transparent; border: none; cursor: pointer;
  width: 32px; height: 32px; border-radius: 8px;
  font-size: 16px; color: var(--text-muted, #64748b);
}
.chat-close:hover { background: var(--accent-soft); color: var(--accent-strong); }

.chat-keyhint {
  margin: 12px 16px 0;
  padding: 10px 14px;
  background: #fef3c7; color: #92400e;
  border: 1px solid #fde68a;
  border-radius: 10px;
  font-size: 13px;
  line-height: 1.5;
}
.chat-keyhint code {
  background: rgba(0,0,0,.08);
  padding: 1px 6px; border-radius: 4px;
  font-size: 12px;
}

.chat-controls {
  display: flex; flex-wrap: wrap; align-items: center; gap: 10px;
  padding: 12px 18px;
  border-bottom: 1px solid var(--border-soft);
  font-size: 12px;
}
.chat-check {
  display: inline-flex; align-items: center; gap: 6px;
  cursor: pointer;
  user-select: none;
}
.chat-check input { accent-color: var(--accent); }
.chat-controls .select.small { padding: 4px 8px; font-size: 12px; height: 30px; }
.chat-controls .btn-ghost.small { padding: 4px 8px; font-size: 14px; line-height: 1; }

.chat-body {
  flex: 1;
  overflow-y: auto;
  padding: 18px;
  display: flex; flex-direction: column; gap: 12px;
  background: var(--bg, #f8fafc);
}

.chat-empty {
  text-align: center;
  padding: 24px 12px;
  color: var(--text-muted, #64748b);
}
.chat-empty-ico { font-size: 42px; }
.chat-empty h3 { margin: 6px 0 8px; color: var(--text); }
.chat-empty p { font-size: 13px; line-height: 1.55; }
.chat-suggests {
  display: flex; flex-wrap: wrap; gap: 6px;
  justify-content: center;
  margin-top: 16px;
}
.chat-suggest {
  background: var(--bg-elev);
  border: 1px solid var(--border-soft);
  color: var(--text);
  padding: 6px 12px;
  border-radius: 999px;
  font-size: 12px;
  cursor: pointer;
  transition: all .15s;
}
.chat-suggest:hover {
  background: var(--accent-soft);
  border-color: var(--accent);
  color: var(--accent-strong);
}

.chat-msg { display: flex; }
.chat-user { justify-content: flex-end; }
.chat-bot  { justify-content: flex-start; }

.chat-bubble {
  max-width: 85%;
  padding: 10px 14px;
  border-radius: 14px;
  font-size: 14px;
  line-height: 1.55;
  word-wrap: break-word;
  white-space: normal;
}
.chat-user .chat-bubble {
  background: linear-gradient(135deg, var(--accent), var(--accent-strong));
  color: #fff;
  border-bottom-right-radius: 4px;
}
.chat-bot .chat-bubble {
  background: var(--bg-elev);
  color: var(--text);
  border: 1px solid var(--border-soft);
  border-bottom-left-radius: 4px;
}
.chat-bubble p { margin: 0 0 8px; }
.chat-bubble p:last-child { margin: 0; }
.chat-bubble ul { margin: 6px 0 8px; padding-left: 18px; }
.chat-bubble li { margin: 2px 0; }
.chat-bubble code {
  background: rgba(99,102,241,.12);
  padding: 1px 5px; border-radius: 4px;
  font-family: 'JetBrains Mono', monospace;
  font-size: 12.5px;
}
.chat-bubble pre {
  background: #0f172a; color: #f1f5f9;
  padding: 10px 12px;
  border-radius: 8px;
  overflow-x: auto;
  margin: 8px 0;
}
.chat-bubble pre code { background: transparent; color: inherit; padding: 0; }
.chat-bubble h3, .chat-bubble h4 { margin: 8px 0 4px; }

.chat-typing {
  display: inline-flex; gap: 4px; align-items: center;
  padding: 4px 0;
}
.chat-typing span {
  width: 6px; height: 6px; border-radius: 50%;
  background: var(--text-muted, #94a3b8);
  animation: chatBlink 1.2s infinite ease-in-out;
}
.chat-typing span:nth-child(2) { animation-delay: .15s; }
.chat-typing span:nth-child(3) { animation-delay: .30s; }
@keyframes chatBlink {
  0%, 80%, 100% { opacity: .25; transform: scale(.8); }
  40% { opacity: 1; transform: scale(1); }
}

.chat-input-row {
  display: flex; gap: 8px;
  padding: 12px 14px;
  border-top: 1px solid var(--border-soft);
  background: var(--bg-elev);
}
.chat-input-row textarea {
  flex: 1;
  resize: none;
  font-family: inherit;
  font-size: 14px;
  line-height: 1.5;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid var(--border-soft);
  background: var(--bg, #fff);
  color: var(--text);
  outline: none;
  transition: border-color .15s;
}
.chat-input-row textarea:focus { border-color: var(--accent); }
.chat-input-row .btn { padding: 0 18px; height: auto; }

/* Hübscher, kompakter Senden-Button (quadratisch, Icon) */
.chat-send {
  flex: 0 0 auto;
  width: 44px;
  height: 44px;
  align-self: flex-end;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--accent), var(--accent-strong));
  color: #fff;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(99, 102, 241, .25);
  transition: transform .12s ease, box-shadow .15s ease, opacity .15s ease;
}
.chat-send:hover:not(:disabled) {
  transform: translateY(-1px) scale(1.03);
  box-shadow: 0 6px 16px rgba(99, 102, 241, .35);
}
.chat-send:active:not(:disabled) { transform: translateY(0) scale(.98); }
.chat-send:disabled { opacity: .45; cursor: not-allowed; box-shadow: none; }
.chat-send svg { display: block; transform: translate(-1px, 1px); }
.chat-send-spinner {
  width: 16px; height: 16px; border-radius: 50%;
  border: 2px solid rgba(255,255,255,.4);
  border-top-color: #fff;
  animation: chatSpin .7s linear infinite;
}
@keyframes chatSpin { to { transform: rotate(360deg); } }

/* Hübsches Modell-Dropdown im Chat-Header */
.chat-model-select {
  flex: 1 1 140px;
  min-width: 140px;
  height: 32px;
  padding: 4px 28px 4px 10px;
  font-size: 12.5px;
  font-weight: 500;
  border-radius: 8px;
  border: 1px solid var(--border-soft);
  background-color: var(--bg-elev);
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8' fill='none'><path d='M1 1l5 5 5-5' stroke='%23667085' stroke-width='1.8' stroke-linecap='round' stroke-linejoin='round'/></svg>");
  background-repeat: no-repeat;
  background-position: right 10px center;
  background-size: 10px 7px;
  color: var(--text);
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  cursor: pointer;
  transition: border-color .15s, box-shadow .15s;
}
.chat-model-select:hover { border-color: var(--accent); }
.chat-model-select:focus {
  border-color: var(--accent);
  box-shadow: 0 0 0 3px rgba(99,102,241,.15);
}
.chat-controls .chat-clear { margin-left: auto; padding: 6px 10px; font-size: 12px; line-height: 1; border-radius: 6px; }

#chatToggle {
  background: linear-gradient(135deg, var(--accent), var(--accent-strong));
  color: #fff;
  border: none;
}
#chatToggle.active {
  box-shadow: 0 0 0 3px var(--accent-soft);
}

/* Floating Action Button für Chat */
.chat-fab {
  position: fixed;
  bottom: 24px;
  right: 24px;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 14px 20px;
  border-radius: 999px;
  background: linear-gradient(135deg, var(--accent), var(--accent-strong));
  color: #fff;
  font-weight: 600;
  font-size: 15px;
  border: none;
  cursor: pointer;
  box-shadow: 0 12px 30px -6px rgba(99, 102, 241, .55), 0 4px 8px -2px rgba(15, 23, 42, .25);
  z-index: 70;
  transition: transform .15s, box-shadow .15s;
}
.chat-fab:hover {
  transform: translateY(-2px);
  box-shadow: 0 16px 36px -6px rgba(99, 102, 241, .65), 0 6px 12px -2px rgba(15, 23, 42, .3);
}
.chat-fab.active {
  background: var(--bg-elev);
  color: var(--accent-strong);
  border: 1px solid var(--border-soft);
}
/* Hide the floating chat toggle while the chat panel is open so it does
   not cover the "Senden" button in the chat input row. The panel can
   still be closed via its × button or the backdrop. */
body.chat-open .chat-fab { display: none; }
.chat-fab-ico { font-size: 22px; line-height: 1; }
@media (max-width: 600px) {
  .chat-fab-label { display: none; }
  .chat-fab { padding: 14px; }
}

/* ===================== RESPONSIVE (handled in mobile block at end of file) ===================== */


/* ============================================================
   Auth overlay (login / register) and account chip
   ============================================================ */
body.auth-locked { overflow: hidden; }
body.auth-locked #app { filter: blur(6px) saturate(0.8); pointer-events: none; user-select: none; }

.auth-overlay {
  position: fixed; inset: 0; z-index: 9999;
  display: none;
  align-items: center; justify-content: center;
  background: rgba(15, 23, 42, 0.55);
  backdrop-filter: blur(8px);
}
.auth-overlay.show { display: flex; }

.auth-card {
  width: min(420px, calc(100vw - 32px));
  background: var(--bg-elev);
  border: 1px solid var(--border);
  border-radius: 18px;
  padding: 28px;
  box-shadow: 0 30px 80px rgba(0,0,0,0.35);
  color: var(--text);
}
.auth-brand { display: flex; align-items: center; gap: 12px; margin-bottom: 18px; }
.auth-brand-logo {
  width: 44px; height: 44px; border-radius: 12px;
  display: grid; place-items: center; font-size: 24px;
  background: linear-gradient(135deg, var(--accent), var(--accent-strong));
}
.auth-brand-title { font-weight: 700; font-size: 18px; }
.auth-brand-sub   { font-size: 12px; color: var(--text-muted); }

.auth-tabs { display: flex; gap: 8px; margin-bottom: 18px; }
.auth-tab {
  flex: 1; padding: 8px 10px; border-radius: 10px;
  background: var(--bg-soft); color: var(--text-muted);
  border: 1px solid transparent; cursor: pointer; font-weight: 600;
}
.auth-tab.active {
  background: var(--accent-soft); color: var(--accent-strong);
  border-color: var(--accent);
}

.auth-title { margin: 0 0 14px; font-size: 20px; }
.auth-label {
  display: block; margin-bottom: 12px;
  font-size: 13px; color: var(--text-muted);
}
.auth-label input {
  display: block; width: 100%; box-sizing: border-box;
  margin-top: 6px; padding: 10px 12px;
  border-radius: 10px; border: 1px solid var(--border);
  background: var(--bg); color: var(--text);
  font: inherit; font-size: 14px;
}
.auth-label input:focus { outline: 2px solid var(--accent); outline-offset: 1px; }
.auth-hint { display: block; margin-top: 4px; font-size: 11px; color: var(--text-muted); }

.auth-error {
  min-height: 1.2em; margin: 4px 0 12px;
  color: #b91c1c; font-size: 13px;
}
:root[data-theme="dark"] .auth-error { color: #fca5a5; }

.auth-submit { width: 100%; padding: 12px; font-size: 15px; }
.auth-note {
  margin: 14px 0 0; font-size: 11px; color: var(--text-muted); text-align: center;
}

/* Sidebar account chip */
.account-chip {
  margin: 12px 16px 16px;
  padding: 10px 12px;
  border: 1px solid var(--border);
  border-radius: 12px;
  background: var(--bg-soft);
  display: flex; align-items: center; gap: 8px;
}
.account-info { flex: 1; min-width: 0; }
.account-email {
  font-size: 12px; font-weight: 600;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.account-sync { font-size: 10px; }

/* ============================================================
   MOBILE OPTIMIZATIONS
   ============================================================ */

/* Mobile topbar (hidden on desktop) */
.mobile-topbar { display: none; }
.sidebar-backdrop { display: none; }

/* iOS safe-area helpers (notch / home-indicator) */
@supports(padding: max(0px)) {
  body { padding-bottom: env(safe-area-inset-bottom); }
}

/* ----- Tablet ----- */
@media (max-width: 1024px) {
  .main { padding: 24px 24px 60px; }
  .exam-row { grid-template-columns: 1fr 100px 160px; }
}

/* ----- Mobile ----- */
@media (max-width: 768px) {
  /* Topbar visible */
  .mobile-topbar {
    display: flex;
    align-items: center;
    gap: 10px;
    position: sticky;
    top: 0;
    z-index: 100;
    padding: 10px 14px;
    padding-top: max(10px, env(safe-area-inset-top));
    background: var(--bg-elev);
    border-bottom: 1px solid var(--border-soft);
    backdrop-filter: saturate(160%) blur(8px);
  }
  .mobile-menu-btn {
    width: 42px; height: 42px;
    border: 1px solid var(--border);
    background: var(--bg-elev);
    border-radius: 10px;
    display: grid; place-items: center;
    cursor: pointer; padding: 0;
  }
  .mm-bar {
    display: block; width: 18px; height: 2px;
    background: var(--text); margin: 2px 0; border-radius: 2px;
    transition: transform .2s, opacity .2s;
  }
  body.sidebar-open .mm-bar:nth-child(1) { transform: translateY(6px) rotate(45deg); }
  body.sidebar-open .mm-bar:nth-child(2) { opacity: 0; }
  body.sidebar-open .mm-bar:nth-child(3) { transform: translateY(-6px) rotate(-45deg); }
  .mobile-brand {
    flex: 1; display: flex; align-items: center; gap: 8px;
    font-weight: 700; font-size: 15px;
  }
  .mobile-brand-logo { font-size: 20px; }
  .mobile-theme-toggle {
    width: 42px; height: 42px;
    border: 1px solid var(--border);
    background: var(--bg-elev);
    border-radius: 10px;
    cursor: pointer; font-size: 18px; padding: 0;
  }

  /* App grid: single column, sidebar becomes off-canvas drawer */
  #app { grid-template-columns: 1fr; }
  .sidebar {
    position: fixed;
    left: 0; top: 0;
    height: 100dvh;
    width: 280px; max-width: 86vw;
    transform: translateX(-100%);
    transition: transform .25s cubic-bezier(.4,.2,.2,1);
    z-index: 200;
    box-shadow: 8px 0 30px -10px rgba(0,0,0,.35);
    padding: 20px 16px;
    padding-top: max(20px, env(safe-area-inset-top));
    padding-bottom: max(20px, env(safe-area-inset-bottom));
    overflow-y: auto;
  }
  body.sidebar-open .sidebar { transform: translateX(0); }
  .nav { flex-direction: column; }
  .nav-item { padding: 12px 14px; font-size: 15px; min-height: 44px; }
  .streak-box { display: block; }
  .theme-toggle { display: none; } /* moved to topbar */

  .sidebar-backdrop {
    display: block;
    position: fixed; inset: 0;
    background: rgba(8, 10, 25, .55);
    backdrop-filter: blur(2px);
    opacity: 0; pointer-events: none;
    transition: opacity .2s;
    z-index: 150;
  }
  body.sidebar-open .sidebar-backdrop { opacity: 1; pointer-events: auto; }
  body.sidebar-open { overflow: hidden; }

  /* Main content */
  .main {
    padding: 16px 14px 100px;
    max-width: 100%;
  }

  /* Page heading: stack title and actions */
  .page-head {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
    margin-bottom: 20px;
  }
  .page-head h1 { font-size: 22px; }
  .head-actions {
    flex-wrap: wrap;
    width: 100%;
  }
  .head-actions .select,
  .head-actions .input,
  .head-actions .btn { flex: 1 1 auto; min-height: 44px; }

  .view-dashboard .stats-grid { grid-template-columns: 1fr; }
  .view-dashboard .deck-card {
    flex-wrap: wrap;
    align-items: flex-start;
    gap: 10px;
  }
  .view-dashboard .deck-meta,
  .view-dashboard .deck-action { width: 100%; }
  .view-dashboard .deck-action {
    text-align: center;
    padding: 8px 10px;
  }

  /* Stats grid: 2 columns on phones */
  .stats-grid { grid-template-columns: 1fr 1fr; gap: 10px; }
  .stat-card { padding: 14px; }
  .stat-value { font-size: 24px; }

  /* Panels */
  .panel { padding: 16px; }
  .panel h2 { font-size: 16px; }

  /* Heatmap: scroll horizontally instead of squishing */
  .heatmap {
    grid-auto-flow: column;
    grid-auto-columns: 14px;
    grid-template-columns: none;
    overflow-x: auto;
    padding-bottom: 4px;
    -webkit-overflow-scrolling: touch;
  }
  .hm-cell { width: 14px; height: 14px; aspect-ratio: auto; }

  /* Review / Flashcard */
  .rp-labels {
    flex-wrap: wrap;
    gap: 8px 12px;
  }
  .card-stage { min-height: auto; margin-bottom: 16px; }
  .card-inner { min-height: 360px; }
  .card-face { padding: 22px 18px; border-radius: 16px; }
  .face-content { font-size: 16px; line-height: 1.55; }
  .card-figure img { max-height: 45vh; }
  .btn-flip { padding: 14px 22px; font-size: 15px; min-height: 48px; width: 100%; max-width: 320px; }

  /* Rating buttons: 2x2 grid, bigger touch targets */
  .rating { grid-template-columns: 1fr 1fr; gap: 8px; }
  .rate-btn { padding: 14px 12px; min-height: 56px; }
  .rate-btn .rl { font-size: 14px; }
  .rate-btn .ri { display: none; } /* hide interval hint to save space */

  /* Browse list: stack rows */
  .browse-row {
    grid-template-columns: 1fr;
    gap: 6px;
    padding: 14px;
  }
  .browse-meta { text-align: left; min-width: 0; }

  /* Analytics tables */
  .chap-row { grid-template-columns: 1fr 50px 50px; padding: 10px; gap: 8px; font-size: 12px; }
  .hardest-row { grid-template-columns: 1fr; gap: 6px; padding: 12px; }

  /* Settings exam-grid */
  .exam-row {
    grid-template-columns: 1fr;
    gap: 10px;
    padding: 14px;
  }
  .exam-target { text-align: left; }
  .setting-row {
    flex-wrap: wrap;
    gap: 10px;
    padding: 14px 0;
  }

  /* Form inputs touch-friendly */
  .select, .input, input[type="date"], input[type="number"] {
    font-size: 16px; /* prevents iOS zoom on focus */
    padding: 11px 12px;
    min-height: 44px;
  }
  .btn { padding: 12px 16px; min-height: 44px; }

  canvas {
    max-width: 100%;
    height: auto;
  }

  /* Chat panel: full screen */
  .chat-backdrop { z-index: 240; }
  .chat-panel {
    width: 100vw;
    max-width: 100vw;
    border-left: none;
  }
  .chat-head {
    padding-top: max(18px, env(safe-area-inset-top));
  }
  .chat-close {
    width: 40px;
    height: 40px;
    font-size: 20px;
  }
  .chat-controls {
    gap: 8px;
    padding: 10px 14px;
  }
  .chat-controls .chat-check,
  .chat-controls .select.small,
  .chat-controls .btn-ghost.small { width: 100%; }
  .chat-controls .select.small { height: 36px; }
  .chat-controls .chat-clear { width: 100%; margin-left: 0; }
  body.chat-open .main { padding-right: 14px; }
  .chat-input-row { padding-bottom: max(12px, env(safe-area-inset-bottom)); }
  .chat-fab {
    bottom: calc(16px + env(safe-area-inset-bottom));
    right: 16px;
    padding: 14px;
    width: 56px; height: 56px;
    justify-content: center;
  }
  .chat-fab-label { display: none; }

  /* Auth overlay */
  .auth-card {
    width: calc(100vw - 24px);
    padding: 22px 20px;
    border-radius: 14px;
    max-height: calc(100dvh - 24px);
    overflow-y: auto;
  }
  .auth-label input { font-size: 16px; min-height: 44px; }
  .auth-submit { min-height: 48px; }

  /* Account chip in sidebar */
  .account-chip { margin: 12px 0 8px; }

  /* Toast */
  .toast { bottom: max(16px, env(safe-area-inset-bottom)); left: 16px; right: 16px; transform: translateY(100px); }
  .toast.show { transform: translateY(0); }
}

/* ----- Very small phones ----- */
@media (max-width: 380px) {
  .stats-grid { grid-template-columns: 1fr; }
  .rating { grid-template-columns: 1fr; }
  .card-face { padding: 18px 14px; }
  .face-content { font-size: 15px; }
  .page-head h1 { font-size: 20px; }
}

/* Allow text inputs to be selectable even when sidebar is open elsewhere */
input, textarea, select { -webkit-user-select: text; user-select: text; }

/* Tap highlight: subtle */
* { -webkit-tap-highlight-color: rgba(99,102,241,.15); }

/* ============================================================
   GAMES / �BUNGEN
   ============================================================ */
.games-grid {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.game-card {
  display: flex; gap: 14px; align-items: flex-start;
  padding: 18px;
  background: var(--bg-elev);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  cursor: pointer;
  text-align: left;
  font-family: inherit;
  color: var(--text);
  transition: transform var(--transition), box-shadow var(--transition), border-color var(--transition);
}
.game-card:hover {
  transform: translateY(-2px);
  box-shadow: var(--shadow);
  border-color: var(--accent);
}
.game-card-ico { font-size: 32px; line-height: 1; flex: 0 0 auto; }
.game-card-body { display: flex; flex-direction: column; gap: 4px; }
.game-card-title { font-weight: 700; font-size: 15px; }
.game-card-sub { color: var(--text-muted); font-size: 13px; }
.game-card-tag { color: var(--accent-strong); font-size: 12px; font-weight: 600; margin-top: 4px; }

.game-stage { background: transparent; }
.game-header { display: flex; gap: 14px; align-items: center; margin-bottom: 18px; }
.game-header-ico { font-size: 36px; }
.game-header-title { margin: 0; font-size: 22px; font-weight: 700; }
.game-header-sub { color: var(--text-muted); font-size: 13px; }

.game-body { display: flex; flex-direction: column; gap: 16px; }

.game-controls {
  display: flex; flex-wrap: wrap; gap: 12px; align-items: flex-end;
  background: var(--bg-elev); border: 1px solid var(--border);
  border-radius: var(--radius); padding: 14px;
}
.game-controls label {
  display: flex; flex-direction: column; gap: 4px; font-size: 12px; color: var(--text-muted);
}
.game-controls .input, .game-controls select {
  font-family: inherit; font-size: 14px;
  padding: 8px 10px; border-radius: 8px;
  border: 1px solid var(--border); background: var(--bg);
  color: var(--text);
}
.game-controls input[type="number"] { width: 90px; padding: 8px 10px; border-radius: 8px; border: 1px solid var(--border); background: var(--bg); color: var(--text); }
.game-info { display: flex; flex-wrap: wrap; gap: 8px; }
.game-info .chip {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 6px 10px; border-radius: 99px;
  background: var(--bg-soft); border: 1px solid var(--border-soft);
  font-size: 13px;
}
.game-actions { display: flex; gap: 10px; flex-wrap: wrap; }
.game-help summary { cursor: pointer; color: var(--accent-strong); font-weight: 600; padding: 6px 0; }
.game-help { background: var(--bg-soft); border: 1px solid var(--border-soft); border-radius: var(--radius); padding: 10px 14px; }
.game-help ul, .game-help ol { margin: 6px 0; padding-left: 22px; }

/* --- Page Replacement --- */
.pr-table-wrap { overflow-x: auto; }
.pr-table {
  border-collapse: collapse; min-width: 100%;
  background: var(--bg-elev); border: 1px solid var(--border);
  border-radius: 8px; overflow: hidden;
}
.pr-table th, .pr-table td {
  padding: 8px 10px; border: 1px solid var(--border-soft);
  text-align: center; font-variant-numeric: tabular-nums;
}
.pr-table thead th { background: var(--bg-soft); font-weight: 600; }
.pr-cur, .pr-cur-cell { background: rgba(99,102,241,.18) !important; color: var(--accent-strong); font-weight: 700; }
.pr-clickable { cursor: pointer; transition: background var(--transition); }
.pr-clickable:hover { background: var(--accent-soft); }
.pr-log { display: flex; flex-direction: column; gap: 4px; font-size: 13px; font-family: 'JetBrains Mono', monospace; }
.pr-final { margin-top: 8px; font-weight: 700; color: var(--success); }

/* --- MMU --- */
.mmu-decompose { display: flex; gap: 10px; align-items: flex-end; flex-wrap: wrap; }
.mmu-deco-step { display: flex; flex-direction: column; gap: 4px; }
.mmu-deco-label { font-size: 12px; color: var(--text-muted); }
.mmu-input { width: 90px; font-family: 'JetBrains Mono', monospace; text-transform: uppercase; }
.mmu-tables { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
@media (max-width: 720px) { .mmu-tables { grid-template-columns: 1fr; } }
.mmu-table { width: 100%; border-collapse: collapse; font-family: 'JetBrains Mono', monospace; font-size: 12px; }
.mmu-table th, .mmu-table td { padding: 4px 8px; border: 1px solid var(--border-soft); text-align: center; }
.mmu-table thead { background: var(--bg-soft); }
.mmu-hl { background: rgba(245,158,11,.18); font-weight: 700; }
.mmu-nil { color: var(--danger); font-weight: 700; }
.mmu-sub { font-size: 13px; margin-bottom: 4px; font-weight: 600; }
.mmu-result { display: flex; flex-wrap: wrap; gap: 10px; align-items: flex-end; }
.mmu-result label { display: flex; flex-direction: column; gap: 4px; font-size: 13px; flex: 1; min-width: 240px; }
.mmu-feedback:empty { display: none; }
.mmu-ok { background: rgba(16,185,129,.12); color: var(--success); padding: 10px 14px; border-radius: 8px; border: 1px solid rgba(16,185,129,.4); }
.mmu-bad { background: rgba(239,68,68,.10); color: var(--danger); padding: 10px 14px; border-radius: 8px; border: 1px solid rgba(239,68,68,.35); }

/* --- Semaphore --- */
.sem-stage { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
@media (max-width: 720px) { .sem-stage { grid-template-columns: 1fr; } }
.sem-proc { background: var(--bg-elev); border: 1px solid var(--border); border-radius: var(--radius); padding: 12px; }
.sem-proc-h { font-weight: 700; margin-bottom: 8px; color: var(--accent-strong); }
.sem-code { display: flex; flex-direction: column; gap: 6px; font-family: 'JetBrains Mono', monospace; font-size: 13px; }
.sem-line { padding: 8px 10px; background: var(--bg-soft); border-radius: 6px; }
.sem-slot { border: 2px dashed var(--border); background: transparent; cursor: pointer; transition: background var(--transition); }
.sem-slot:hover { background: var(--bg-soft); }
.sem-filled { border-style: solid; border-color: var(--accent); background: var(--accent-soft); color: var(--accent-strong); font-weight: 600; }
.sem-bank { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 10px; }
.sem-chip {
  font-family: 'JetBrains Mono', monospace; font-size: 12px;
  padding: 6px 10px; border-radius: 6px; cursor: pointer;
  background: var(--accent); color: #fff; border: 0;
}
.sem-chip:disabled, .sem-chip-used { opacity: .35; cursor: not-allowed; }
.sem-explain { font-size: 13px; color: var(--text-muted); }
.sem-graph pre { font-family: 'JetBrains Mono', monospace; background: var(--bg-soft); padding: 10px; border-radius: 8px; }

/* --- RAID --- */
.raid-result { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 12px; }
.raid-card { background: var(--bg-elev); border: 1px solid var(--border); border-radius: var(--radius); padding: 14px; }
.raid-label { color: var(--text-muted); font-size: 13px; }
.raid-val { font-size: 26px; font-weight: 700; margin-top: 4px; }
.raid-formula { font-family: 'JetBrains Mono', monospace; font-size: 12px; color: var(--text-muted); margin-top: 6px; }
.raid-explain { font-size: 13px; color: var(--text-muted); }
.raid-quiz-row { display: flex; gap: 10px; margin: 10px 0; }
.raid-quiz h3 { margin: 0 0 8px 0; }

/* --- Buddy --- */
.buddy-bar { display: flex; min-height: 60px; gap: 2px; padding: 4px; background: var(--bg-soft); border-radius: 8px; border: 1px solid var(--border-soft); }
.buddy-split { display: flex; gap: 2px; flex: 1; }
.buddy-leaf { display: flex; align-items: center; justify-content: center; padding: 8px; border-radius: 6px; font-size: 12px; min-width: 30px; }
.buddy-leaf.buddy-free { background: rgba(16,185,129,.15); color: var(--success); border: 1px dashed rgba(16,185,129,.5); }
.buddy-leaf.buddy-alloc { background: rgba(99,102,241,.18); color: var(--accent-strong); border: 1px solid var(--accent); font-weight: 600; }

/* --- Banker --- */
.bk-question { background: var(--bg-elev); border: 1px solid var(--border); border-radius: 8px; padding: 12px; }
.bk-question .input { margin-top: 6px; }

/* --- Scheduling --- */
.gantt-bar { display: flex; height: 44px; border-radius: 6px; overflow: hidden; border: 1px solid var(--border); }
.gantt-seg { color: #fff; font-size: 12px; display: flex; align-items: center; justify-content: center; font-weight: 600; }
.gantt-axis { position: relative; height: 18px; margin-top: 4px; }
.gantt-axis span { position: absolute; transform: translateX(-50%); font-size: 11px; color: var(--text-muted); }

/* --- Disk Sched --- */
.ds-svg text { fill: var(--text); }

/* --- Fit table --- */
.fit-table .select.small { font-size: 12px; padding: 4px 6px; }

@media (max-width: 720px) {
  .game-controls { gap: 8px; padding: 10px; }
}


/* ===================== SUMMARIES / ZUSAMMENFASSUNGEN ===================== */
.summary-layout {
  display: grid;
  grid-template-columns: 240px 1fr;
  gap: 22px;
  align-items: start;
}
.summary-toc {
  position: sticky;
  top: 24px;
  max-height: calc(100vh - 64px);
  overflow-y: auto;
  background: var(--bg-elev);
  border: 1px solid var(--border-soft);
  border-radius: var(--radius);
  padding: 16px 12px;
  box-shadow: var(--shadow-sm);
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.summary-toc .toc-title {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: .08em;
  color: var(--text-muted);
  font-weight: 700;
  padding: 4px 8px 8px;
  border-bottom: 1px solid var(--border-soft);
  margin-bottom: 6px;
}
.summary-toc a {
  display: block;
  text-decoration: none;
  color: var(--text);
  font-size: 13px;
  padding: 5px 10px;
  border-radius: 7px;
  border-left: 2px solid transparent;
  /* Allow long chapter names to wrap onto multiple lines so the TOC
     stays fully readable (fixes the squashed "dots & dashes" look on
     decks with many long headings, e.g. Betriebssysteme). */
  white-space: normal;
  overflow-wrap: anywhere;
  line-height: 1.35;
  transition: background var(--transition), color var(--transition),
              border-color var(--transition);
}
.summary-toc a:hover {
  background: var(--bg-soft);
  color: var(--accent);
  border-left-color: var(--accent);
}
.summary-toc a.toc-l1 {
  font-weight: 700;
  color: var(--text);
  padding-left: 10px;
  margin-top: 4px;
}
.summary-toc a.toc-l2 {
  font-weight: 600;
  color: var(--text);
  padding-left: 10px;
  margin-top: 4px;
}
.summary-toc a.toc-l3 {
  padding-left: 22px;
  font-size: 12.5px;
  color: var(--text-muted);
}
.summary-toc a.toc-l4 {
  padding-left: 34px;
  font-size: 12px;
  color: var(--text-muted);
}

.summary-content {
  background: var(--bg-elev);
  border: 1px solid var(--border-soft);
  border-radius: var(--radius);
  padding: 36px 44px;
  box-shadow: var(--shadow-sm);
  min-height: 400px;
}
.summary-empty {
  text-align: center;
  padding: 80px 20px;
  font-size: 14px;
}

.summary-doc {
  font-size: 15px;
  line-height: 1.7;
  color: var(--text);
  max-width: 78ch;
  margin: 0 auto;
}
.summary-doc h1 {
  font-size: 30px;
  font-weight: 800;
  letter-spacing: -.02em;
  margin: 0 0 28px;
  padding-bottom: 14px;
  border-bottom: 2px solid var(--border-soft);
}
.summary-doc h2 {
  font-size: 22px;
  font-weight: 700;
  letter-spacing: -.01em;
  margin: 36px 0 14px;
  padding-left: 12px;
  border-left: 4px solid var(--accent);
  scroll-margin-top: 24px;
}
.summary-doc h3 {
  font-size: 17px;
  font-weight: 700;
  margin: 24px 0 8px;
  color: var(--text);
  scroll-margin-top: 24px;
}
.summary-doc h4 {
  font-size: 15px;
  font-weight: 700;
  margin: 18px 0 6px;
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: .05em;
  scroll-margin-top: 24px;
}
.summary-doc p { margin: 10px 0; }
.summary-doc strong { color: var(--text); font-weight: 700; }
.summary-doc em { color: var(--text); }
.summary-doc ul, .summary-doc ol {
  margin: 10px 0 14px;
  padding-left: 24px;
}
.summary-doc li { margin: 4px 0; }
.summary-doc li::marker { color: var(--accent); }
.summary-doc code {
  background: var(--bg-soft);
  border: 1px solid var(--border-soft);
  border-radius: 6px;
  padding: 1px 6px;
  font-family: 'JetBrains Mono', ui-monospace, Consolas, monospace;
  font-size: .88em;
  color: var(--accent-strong);
}
.summary-doc pre {
  background: var(--bg-soft);
  border: 1px solid var(--border-soft);
  border-radius: 10px;
  padding: 14px 16px;
  overflow-x: auto;
  margin: 14px 0;
}
.summary-doc pre code {
  background: transparent;
  border: 0;
  padding: 0;
  color: var(--text);
  font-size: 13px;
}
.summary-doc blockquote {
  margin: 14px 0;
  padding: 10px 16px;
  border-left: 4px solid var(--accent);
  background: var(--accent-soft);
  border-radius: 0 8px 8px 0;
  color: var(--text);
}
.summary-doc hr {
  border: 0;
  border-top: 1px solid var(--border-soft);
  margin: 28px 0;
}
.summary-doc a {
  color: var(--accent-strong);
  text-decoration: underline;
  text-underline-offset: 2px;
}

@media (max-width: 980px) {
  .summary-layout { grid-template-columns: 1fr; }
  .summary-toc {
    position: static;
    max-height: 220px;
  }
  .summary-content { padding: 24px 20px; }
}

@media print {
  .sidebar, .mobile-topbar, .chat-fab, .chat-panel, .summary-toc,
  .head-actions, #toast { display: none !important; }
  #app { display: block; }
  .main { padding: 0; max-width: none; }
  .view { display: none; }
  .view-summaries.active { display: block; }
  .summary-layout { grid-template-columns: 1fr; }
  .summary-content {
    border: 0; box-shadow: none; padding: 0;
    background: #fff; color: #000;
  }
  .summary-doc { max-width: none; color: #000; }
  .summary-doc h2 { border-left-color: #000; }
}


/* ============================================================
   GAMES – InfoSec / Filter UI
   ============================================================ */
.games-filter {
  display: flex; flex-wrap: wrap; gap: 8px;
  margin-bottom: 18px;
}
.games-filter-chip {
  display: inline-flex; align-items: center; gap: 8px;
  padding: 8px 14px;
  border-radius: 99px;
  border: 1px solid var(--border);
  background: var(--bg-elev);
  color: var(--text-muted);
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all var(--transition);
}
.games-filter-chip:hover { border-color: var(--accent); color: var(--text); }
.games-filter-chip.active {
  background: linear-gradient(135deg, var(--accent), var(--accent-strong));
  color: #fff;
  border-color: transparent;
  box-shadow: 0 4px 12px -3px rgba(99,102,241,.45);
}
.games-filter-count {
  display: inline-grid; place-items: center;
  min-width: 22px; height: 22px;
  padding: 0 6px;
  border-radius: 99px;
  background: var(--bg-soft);
  color: var(--text-muted);
  font-size: 11px;
  font-weight: 700;
}
.games-filter-chip.active .games-filter-count {
  background: rgba(255,255,255,.25);
  color: #fff;
}
.games-cards {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 16px;
}

/* === Generic Status Bar (Phishing / Caesar / Memory / PortHunter) === */
.ph-status {
  display: flex; flex-wrap: wrap; gap: 10px;
  padding: 12px 14px;
  background: var(--bg-elev);
  border: 1px solid var(--border-soft);
  border-radius: var(--radius);
  align-items: center;
}
.ph-stat {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 6px 12px;
  background: var(--bg-soft);
  border-radius: 99px;
  font-size: 13px;
}
.ph-stat b { font-weight: 800; }
.ph-score b { color: var(--accent-strong); }
.ph-combo b { color: var(--warn); }
.ph-lives { letter-spacing: 2px; }

.ph-end {
  text-align: center;
  padding: 36px 20px;
  background: var(--bg-elev);
  border: 1px solid var(--border-soft);
  border-radius: var(--radius);
}
.ph-end-grade { font-size: 26px; font-weight: 800; margin-bottom: 8px; }
.ph-end-score { font-size: 18px; color: var(--text-muted); margin-bottom: 14px; }

/* === Phishing === */
.ph-mail {
  background: var(--bg-elev);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  overflow: hidden;
  box-shadow: var(--shadow-sm);
}
.ph-mail-head {
  background: var(--bg-soft);
  padding: 14px 18px;
  display: flex; flex-direction: column; gap: 6px;
  font-size: 13px;
  border-bottom: 1px solid var(--border-soft);
}
.ph-mail-head .ph-l { color: var(--text-muted); font-size: 11px; text-transform: uppercase; letter-spacing: .05em; margin-right: 6px; }
.ph-mail-head code { background: transparent; color: var(--text); font-family: 'JetBrains Mono', monospace; font-size: 13px; word-break: break-all; }
.ph-mail-body {
  padding: 18px;
  font-size: 14px;
  line-height: 1.55;
  white-space: pre-wrap;
}
.ph-actions { display: flex; gap: 12px; flex-wrap: wrap; }
.ph-btn { flex: 1 1 180px; padding: 16px; font-size: 16px; font-weight: 700; border-radius: 12px; }
.ph-btn-phish { background: var(--danger); color: #fff; border: 0; }
.ph-btn-phish:hover { background: #dc2626; }
.ph-btn-legit { background: var(--success); color: #fff; border: 0; }
.ph-btn-legit:hover { background: #059669; }

.ph-reveal {
  padding: 18px 20px;
  border-radius: var(--radius);
  background: var(--bg-elev);
  border: 1px solid var(--border-soft);
  border-left-width: 6px;
}
.ph-reveal.ok  { border-left-color: var(--success); }
.ph-reveal.bad { border-left-color: var(--danger); }
.ph-reveal-head { font-size: 16px; font-weight: 700; margin-bottom: 10px; }
.ph-reveal-flags ul { margin: 6px 0 14px; padding-left: 22px; }

/* === Caesar === */
.ca-cipher {
  font-family: 'JetBrains Mono', monospace;
  font-size: 18px;
  letter-spacing: 3px;
  background: var(--bg-elev);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 18px;
  text-align: center;
  word-break: break-word;
  color: var(--accent-strong);
  font-weight: 600;
}
.ca-wheel {
  display: grid;
  grid-template-columns: auto 1fr auto;
  gap: 12px;
  align-items: center;
  background: var(--bg-elev);
  border: 1px solid var(--border-soft);
  border-radius: var(--radius);
  padding: 14px 18px;
}
.ca-wheel .btn { font-size: 22px; padding: 8px 18px; }
.ca-shift { text-align: center; }
.ca-shift-label { font-size: 11px; text-transform: uppercase; letter-spacing: .05em; color: var(--text-muted); }
.ca-shift-val { font-size: 32px; font-weight: 800; color: var(--accent-strong); line-height: 1; }
.ca-slider { grid-column: 1 / -1; width: 100%; accent-color: var(--accent); }
.ca-plain {
  font-family: 'JetBrains Mono', monospace;
  font-size: 16px;
  letter-spacing: 2px;
  background: var(--bg-soft);
  border: 1px solid var(--border-soft);
  border-radius: var(--radius);
  padding: 16px;
  text-align: center;
  word-break: break-word;
  transition: all var(--transition);
}
.ca-plain.ok {
  background: rgba(16,185,129,.12);
  border-color: var(--success);
  color: var(--success);
  font-weight: 700;
}
.ca-hintbox {
  background: var(--bg-soft);
  border: 1px solid var(--border-soft);
  border-radius: var(--radius);
  padding: 12px 14px;
  font-size: 13px;
}
.ca-hintbox .chip { margin: 0 4px; font-family: monospace; }

/* === Port-Hunter === */
.phh-q {
  background: var(--bg-elev);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 26px;
  text-align: center;
}
.phh-svc { font-size: 38px; font-weight: 800; letter-spacing: -.02em; color: var(--accent-strong); }
.phh-hint { font-size: 14px; margin-top: 6px; }
.phh-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; }
.phh-opt {
  font-family: 'JetBrains Mono', monospace;
  font-size: 22px;
  font-weight: 700;
  padding: 22px;
  border-radius: 14px;
  border: 2px solid var(--border);
  background: var(--bg-elev);
  color: var(--text);
  cursor: pointer;
  transition: all 150ms ease;
}
.phh-opt:hover { border-color: var(--accent); transform: translateY(-2px); }
.phh-opt.ok  { background: var(--success); color: #fff; border-color: transparent; transform: scale(1.05); }
.phh-opt.bad { background: var(--danger); color: #fff; border-color: transparent; animation: shake .3s; }
@keyframes shake { 0%, 100% { transform: translateX(0); } 25% { transform: translateX(-4px); } 75% { transform: translateX(4px); } }

/* === Malware Memory === */
.mm-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 10px;
}
.mm-card {
  position: relative;
  height: 110px;
  border-radius: 12px;
  border: 0;
  cursor: pointer;
  font-family: inherit;
  perspective: 600px;
  background: transparent;
  padding: 0;
}
.mm-front, .mm-back {
  position: absolute; inset: 0;
  display: grid; place-items: center;
  border-radius: 12px;
  padding: 10px;
  font-size: 13px;
  text-align: center;
  backface-visibility: hidden;
  transition: transform 400ms cubic-bezier(.4, 0, .2, 1);
}
.mm-front {
  background: linear-gradient(135deg, var(--accent), var(--pink));
  color: #fff;
  font-size: 32px;
  font-weight: 800;
}
.mm-back {
  background: var(--bg-elev);
  border: 1px solid var(--border);
  color: var(--text);
  transform: rotateY(180deg);
  font-weight: 600;
  line-height: 1.3;
}
.mm-card.flip .mm-front { transform: rotateY(180deg); }
.mm-card.flip .mm-back  { transform: rotateY(360deg); }
.mm-card.matched .mm-back {
  background: rgba(16,185,129,.15);
  border-color: var(--success);
  color: var(--success);
}
.mm-k .mm-back { font-weight: 800; font-size: 15px; }
@media (max-width: 720px) { .mm-grid { grid-template-columns: repeat(2, 1fr); } }

/* === RSA Dojo === */
.rsa-grid { display: grid; gap: 14px; }
.rsa-step {
  position: relative;
  background: var(--bg-elev);
  border: 1px solid var(--border-soft);
  border-radius: var(--radius);
  padding: 18px 20px 18px 64px;
  box-shadow: var(--shadow-sm);
}
.rsa-num {
  position: absolute; left: 16px; top: 16px;
  width: 36px; height: 36px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--accent), var(--accent-strong));
  color: #fff;
  display: grid; place-items: center;
  font-weight: 800;
  font-size: 16px;
  box-shadow: var(--shadow-sm);
}
.rsa-title { font-weight: 700; margin-bottom: 8px; }
.rsa-eq {
  font-family: 'JetBrains Mono', monospace;
  font-size: 14px;
  background: var(--bg-soft);
  border-radius: 8px;
  padding: 8px 12px;
  margin: 4px 0;
}
.rsa-eq b { color: var(--accent-strong); }
.rsa-eq b.ok { color: var(--success); }
.rsa-row { display: flex; gap: 14px; flex-wrap: wrap; align-items: center; }
.rsa-row label { display: flex; flex-direction: column; gap: 4px; font-size: 12px; color: var(--text-muted); }
.rsa-row select, .rsa-row input { padding: 8px 10px; border-radius: 8px; border: 1px solid var(--border); background: var(--bg); color: var(--text); font-family: inherit; }
.rsa-keys { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-top: 10px; }
.rsa-key { padding: 10px 14px; border-radius: 10px; font-family: 'JetBrains Mono', monospace; font-size: 13px; font-weight: 600; }
.rsa-pub  { background: rgba(16,185,129,.12); color: var(--success); border: 1px solid rgba(16,185,129,.3); }
.rsa-priv { background: rgba(239,68,68,.10); color: var(--danger); border: 1px solid rgba(239,68,68,.3); }
.bad { color: var(--danger); font-weight: 600; }
@media (max-width: 720px) { .rsa-keys { grid-template-columns: 1fr; } }

/* === Diffie-Hellman Lab === */
.dh-public {
  background: var(--bg-elev);
  border: 1px solid var(--border-soft);
  border-radius: var(--radius);
  padding: 14px 18px;
}
.dh-pub-title { font-weight: 700; margin-bottom: 8px; font-size: 14px; }
.dh-row { display: flex; gap: 14px; flex-wrap: wrap; }
.dh-row label { display: flex; flex-direction: column; gap: 4px; font-size: 12px; color: var(--text-muted); }
.dh-row select, .dh-row input { padding: 8px 10px; border-radius: 8px; border: 1px solid var(--border); background: var(--bg); color: var(--text); font-family: inherit; }
.dh-grid {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  gap: 14px;
  align-items: stretch;
}
.dh-party {
  background: var(--bg-elev);
  border: 1px solid var(--border-soft);
  border-radius: var(--radius);
  padding: 16px;
}
.dh-party-title { font-weight: 700; font-size: 16px; margin-bottom: 10px; }
.dh-alice .dh-party-title { color: var(--pink); }
.dh-bob   .dh-party-title { color: var(--accent-strong); }
.dh-party label { display: flex; flex-direction: column; gap: 4px; font-size: 12px; color: var(--text-muted); margin-bottom: 8px; }
.dh-party input { padding: 8px 10px; border-radius: 8px; border: 1px solid var(--border); background: var(--bg); color: var(--text); font-family: inherit; }
.dh-eq {
  font-family: 'JetBrains Mono', monospace;
  font-size: 12.5px;
  background: var(--bg-soft);
  border-radius: 6px;
  padding: 6px 10px;
  margin: 4px 0;
  word-break: break-all;
}
.dh-eq b.ok { color: var(--success); font-weight: 800; }
.dh-arrows { display: flex; flex-direction: column; gap: 12px; align-items: center; justify-content: center; min-width: 160px; }
.dh-arrow {
  font-family: 'JetBrains Mono', monospace;
  font-size: 12px;
  padding: 6px 12px;
  background: var(--bg-soft);
  border-radius: 99px;
  color: var(--text-muted);
}
.dh-shared {
  padding: 10px 14px;
  border-radius: 10px;
  font-weight: 700;
  font-size: 13px;
  text-align: center;
}
.dh-shared.ok  { background: rgba(16,185,129,.15); color: var(--success); border: 1px solid rgba(16,185,129,.4); }
.dh-shared.bad { background: rgba(239,68,68,.12); color: var(--danger); border: 1px solid rgba(239,68,68,.4); }
@media (max-width: 980px) {
  .dh-grid { grid-template-columns: 1fr; }
  .dh-arrows { flex-direction: row; flex-wrap: wrap; }
}

/* === Square & Multiply === */
.sqm-table {
  width: 100%;
  border-collapse: collapse;
  background: var(--bg-elev);
  border: 1px solid var(--border);
  border-radius: 8px;
  overflow: hidden;
  font-size: 14px;
}
.sqm-table th, .sqm-table td {
  padding: 8px 12px;
  border-bottom: 1px solid var(--border-soft);
  text-align: left;
}
.sqm-table th { background: var(--bg-soft); font-weight: 700; font-size: 12px; text-transform: uppercase; letter-spacing: .04em; color: var(--text-muted); }
.sqm-table tr.square   td { color: var(--accent-strong); }
.sqm-table tr.multiply { background: rgba(99,102,241,.05); }
.sqm-table tr.multiply td { color: var(--pink); font-weight: 600; }
.sqm-table code { background: var(--bg-soft); padding: 2px 6px; border-radius: 4px; font-family: 'JetBrains Mono', monospace; }

/* === ECB vs CBC === */
.ecb-side {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 18px;
  align-items: start;
}
.ecb-h { font-weight: 700; margin-bottom: 8px; font-size: 14px; }
.ecb-svg { width: 100%; max-width: 260px; height: auto; border-radius: 8px; border: 1px solid var(--border-soft); background: var(--bg-elev); }
.ecb-explain {
  padding: 14px 18px;
  border-radius: var(--radius);
  font-size: 14px;
  line-height: 1.5;
  border-left: 4px solid var(--border-soft);
}
.ecb-explain.ok  { background: rgba(16,185,129,.10); border-left-color: var(--success); }
.ecb-explain.bad { background: rgba(239,68,68,.08); border-left-color: var(--danger); }
@media (max-width: 720px) { .ecb-side { grid-template-columns: 1fr; } }

/* ============================================================
   MOBILE - Uebungen / Games  (must come last to win cascade)
   ============================================================ */
@media (max-width: 768px) {
  /* Filter chips: horizontal scroll instead of ugly wrap */
  .games-filter {
    flex-wrap: nowrap;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    padding-bottom: 6px;
    scrollbar-width: none;
  }
  .games-filter::-webkit-scrollbar { display: none; }
  .games-filter-chip { flex-shrink: 0; padding: 7px 12px; font-size: 12px; }
  .games-filter-count { min-width: 18px; height: 18px; font-size: 10px; }

  /* Cards: single column */
  .games-cards { grid-template-columns: 1fr; }
  .game-card { padding: 14px; gap: 12px; }
  .game-card-ico { font-size: 26px; }

  /* Game stage header */
  .game-header { flex-wrap: wrap; gap: 8px; margin-bottom: 14px; }
  .game-header-ico { font-size: 28px; }
  .game-header-title { font-size: 18px; }

  /* Controls: stack vertically, full-width inputs */
  .game-controls {
    flex-direction: column;
    gap: 10px;
    padding: 12px;
  }
  .game-controls label { width: 100%; }
  .game-controls .input,
  .game-controls select,
  .game-controls input[type='number'] {
    width: 100%;
    font-size: 16px;
    min-height: 44px;
  }
  .game-actions { flex-direction: column; }
  .game-actions .btn { width: 100%; min-height: 44px; }

  /* MMU */
  .mmu-result label { min-width: 0; flex: 1 1 100%; }
  .mmu-input { width: 100%; }

  /* RAID */
  .raid-result { grid-template-columns: 1fr; }
  .raid-quiz-row { flex-direction: column; gap: 8px; }

  /* RSA */
  .rsa-step { padding: 14px 12px 14px 48px; }
  .rsa-num { width: 28px; height: 28px; font-size: 12px; left: 10px; top: 14px; }
  .rsa-keys { grid-template-columns: 1fr; }

  /* Port-Hunter */
  .phh-grid { grid-template-columns: 1fr 1fr; gap: 8px; }
  .phh-opt { font-size: 18px; padding: 16px 8px; }
  .phh-svc { font-size: 28px; }
  .phh-q { padding: 18px 14px; }

  /* Caesar */
  .ca-cipher, .ca-plain { font-size: 15px; letter-spacing: 1px; padding: 14px; }

  /* Buddy bar: scroll horizontally */
  .buddy-bar { overflow-x: auto; -webkit-overflow-scrolling: touch; }

  /* Gantt */
  .gantt-bar { height: 36px; }
  .gantt-seg { font-size: 11px; }

  /* Page-replacement table */
  .pr-table th, .pr-table td { padding: 5px 6px; font-size: 12px; }
}

@media (max-width: 380px) {
  .games-filter-chip { padding: 6px 10px; font-size: 11px; }
  .phh-grid { grid-template-columns: 1fr; }
  .phh-opt { font-size: 16px; padding: 14px 8px; }
  .game-header-title { font-size: 16px; }
}

/* ============================================================
   THE SYNCING GAME — Drag & Drop Semaphor-Synchronisation
   ============================================================ */

/* ── Toolbar ── */
.sync-toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  background: linear-gradient(135deg,
    color-mix(in srgb, var(--accent) 8%, var(--bg-elev)),
    var(--bg-elev));
  border: 1px solid var(--border);
  border-radius: var(--radius);
  margin-bottom: 14px;
}
.sync-toolbar-label {
  display: flex; align-items: center; gap: 10px;
  font-size: 13px; color: var(--text-muted);
  font-weight: 600;
}
.sync-toolbar-sel {
  background: var(--bg);
  color: var(--text);
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 7px 10px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
}
.sync-toolbar-info { display: flex; gap: 8px; flex-wrap: wrap; }
.sync-toolbar-gen {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}
.sync-toolbar-btn {
  padding: 8px 14px;
  font-size: 13px;
  font-weight: 600;
  border-radius: 8px;
  white-space: nowrap;
}
.sync-pill {
  font-size: 12px;
  background: var(--bg);
  border: 1px solid var(--border);
  border-radius: 999px;
  padding: 5px 11px;
  color: var(--text-muted);
}
.sync-pill b { color: var(--text); margin-right: 2px; }

/* ── Regeln (collapsible) ── */
.sync-rules {
  background: var(--bg-elev);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  margin-bottom: 14px;
}
.sync-rules > summary {
  padding: 12px 16px;
  cursor: pointer;
  font-weight: 600;
  font-size: 14px;
  user-select: none;
  list-style: none;
}
.sync-rules > summary::-webkit-details-marker { display: none; }
.sync-rules > summary::before {
  content: '▸';
  display: inline-block;
  margin-right: 8px;
  transition: transform 0.18s ease;
  color: var(--accent);
}
.sync-rules[open] > summary::before { transform: rotate(90deg); }
.sync-rules-body {
  padding: 0 16px 14px;
  font-size: 13px;
  line-height: 1.55;
  color: var(--text-muted);
}
.sync-rules-body p { margin: 6px 0; }
.sync-rules-body ol { margin: 8px 0 0 20px; padding: 0; }
.sync-rules-body li { margin: 3px 0; }

/* ── Bank + Trash ── */
.sync-bank-wrap {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 12px;
  margin-bottom: 18px;
}
.sync-bank {
  background: linear-gradient(135deg,
    color-mix(in srgb, var(--accent) 5%, var(--bg-elev)),
    var(--bg-elev));
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 14px 16px;
  box-shadow: 0 1px 2px rgba(0,0,0,0.03), 0 4px 12px rgba(0,0,0,0.04);
}
.sync-bank-head {
  display: flex; justify-content: space-between; align-items: baseline;
  margin-bottom: 12px; gap: 10px; flex-wrap: wrap;
}
.sync-bank-title { font-weight: 700; font-size: 14px; color: var(--text); }
.sync-bank-hint  { font-size: 12px; color: var(--text-muted); font-style: italic; }
.sync-bank-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 10px;
}
.sync-bank-pair {
  display: flex; align-items: center; gap: 8px;
  padding: 8px 10px;
  background: var(--bg);
  border: 1px solid var(--border);
  border-left: 3px solid var(--sem-color);
  border-radius: 8px;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}
.sync-bank-pair:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 10px color-mix(in srgb, var(--sem-color) 25%, transparent);
}
.sync-bank-label {
  font-weight: 700;
  font-size: 13px;
  color: var(--sem-color);
  min-width: 22px;
}

/* ── Trash zone ── */
.sync-trash {
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  gap: 4px;
  min-width: 88px;
  padding: 12px 14px;
  background: var(--bg-elev);
  border: 2px dashed var(--border);
  border-radius: var(--radius);
  color: var(--text-muted);
  font-size: 11px;
  text-align: center;
  transition: all 0.18s ease;
}
.sync-trash-icon { font-size: 24px; line-height: 1; }
.sync-trash-hover {
  background: color-mix(in srgb, var(--danger) 15%, var(--bg-elev));
  border-color: var(--danger);
  color: var(--danger);
  transform: scale(1.05);
  box-shadow: 0 0 0 4px color-mix(in srgb, var(--danger) 18%, transparent);
}

/* ── Operations chip (P/V) ── */
.sync-op {
  display: inline-flex; align-items: center;
  gap: 1px;
  font-family: 'JetBrains Mono', 'Consolas', ui-monospace, monospace;
  font-size: 12.5px;
  font-weight: 700;
  padding: 5px 10px;
  border-radius: 7px;
  cursor: grab;
  user-select: none;
  background: linear-gradient(135deg,
    color-mix(in srgb, var(--sem-color) 20%, var(--bg)),
    color-mix(in srgb, var(--sem-color) 8%, var(--bg)));
  color: var(--sem-color);
  border: 1.5px solid color-mix(in srgb, var(--sem-color) 50%, transparent);
  transition: transform 0.12s ease, box-shadow 0.12s ease, opacity 0.12s ease;
  white-space: nowrap;
}
.sync-op:hover {
  transform: translateY(-2px) scale(1.03);
  box-shadow: 0 6px 14px color-mix(in srgb, var(--sem-color) 35%, transparent);
}
.sync-op:active { cursor: grabbing; }
.sync-op-letter { font-weight: 800; letter-spacing: 0.3px; }
.sync-op-paren  { opacity: 0.6; font-weight: 600; }
.sync-op sub { font-size: 0.75em; opacity: 0.85; }
.sync-op-P { /* base style above */ }
.sync-op-V {
  background: linear-gradient(135deg,
    color-mix(in srgb, var(--sem-color) 30%, var(--bg)),
    color-mix(in srgb, var(--sem-color) 14%, var(--bg)));
}
.sync-op-dragging {
  opacity: 0.45;
  transform: scale(0.94);
  cursor: grabbing;
}
.sync-op-tapselected {
  outline: 2px solid var(--sem-color);
  outline-offset: 2px;
  box-shadow: 0 0 0 4px color-mix(in srgb, var(--sem-color) 20%, transparent);
}

/* Pop-in animation when an op is dropped/placed */
@keyframes syncOpPop {
  0%   { transform: scale(0.4) rotate(-6deg); opacity: 0; }
  60%  { transform: scale(1.18) rotate(2deg);  opacity: 1; }
  100% { transform: scale(1)    rotate(0);     opacity: 1; }
}
.sync-op-pop {
  animation: syncOpPop 0.38s cubic-bezier(.34,1.56,.64,1);
}

@keyframes syncOpRemove {
  to { transform: scale(0.3) rotate(8deg); opacity: 0; }
}
.sync-op-removing { animation: syncOpRemove 0.18s ease-in forwards; }

/* ── Process board ── */
.sync-board {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 14px;
  margin-bottom: 14px;
}
.sync-proc {
  background: linear-gradient(180deg,
    color-mix(in srgb, var(--proc-color) 5%, var(--bg-elev)),
    var(--bg-elev));
  border: 1px solid var(--border);
  border-top: 3px solid var(--proc-color);
  border-radius: var(--radius);
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.sync-proc-head {
  display: flex; align-items: center; gap: 8px;
  font-weight: 700;
  font-size: 13px;
  color: var(--text);
  padding-bottom: 8px;
  border-bottom: 1px dashed var(--border);
  margin-bottom: 4px;
}
.sync-proc-dot {
  width: 8px; height: 8px;
  border-radius: 50%;
  background: var(--proc-color);
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--proc-color) 22%, transparent);
}
.sync-proc-body {
  display: flex; flex-direction: column; gap: 4px;
}

/* ── Code block (krit. / frei) ── */
.sync-block {
  display: flex; align-items: center; justify-content: space-between;
  padding: 10px 12px;
  border-radius: 8px;
  font-family: 'JetBrains Mono', 'Consolas', ui-monospace, monospace;
  font-weight: 700;
  font-size: 15px;
}
.sync-block-letter { font-size: 18px; }
.sync-block-tag {
  font-size: 9.5px;
  font-weight: 700;
  letter-spacing: 0.5px;
  padding: 2px 6px;
  border-radius: 999px;
  text-transform: uppercase;
  background: color-mix(in srgb, var(--accent) 22%, transparent);
  color: var(--accent);
  font-family: var(--font-sans, system-ui);
}
.sync-block-tag-free {
  background: color-mix(in srgb, var(--text-muted) 18%, transparent);
  color: var(--text-muted);
}
.sync-block-crit {
  background: linear-gradient(135deg,
    color-mix(in srgb, var(--accent) 18%, var(--bg)),
    color-mix(in srgb, var(--accent) 6%, var(--bg)));
  border: 1px solid color-mix(in srgb, var(--accent) 40%, transparent);
  color: var(--accent);
}
.sync-block-free {
  background: var(--bg);
  border: 1px dashed var(--border);
  color: var(--text-muted);
}

/* ── Drop slot (gap between blocks) ── */
.sync-slot {
  display: flex; flex-wrap: wrap; align-items: center;
  gap: 4px;
  min-height: 36px;
  padding: 4px 8px;
  border-radius: 6px;
  background: color-mix(in srgb, var(--text-muted) 4%, transparent);
  border: 1.5px dashed color-mix(in srgb, var(--text-muted) 25%, transparent);
  transition: all 0.15s ease;
}
.sync-slot-empty {
  justify-content: center;
}
.sync-slot-hint {
  font-size: 16px;
  color: color-mix(in srgb, var(--text-muted) 60%, transparent);
  font-weight: 300;
}
.sync-slot-full {
  background: color-mix(in srgb, var(--accent) 6%, transparent);
}
.sync-slot-hover {
  background: color-mix(in srgb, var(--accent) 15%, transparent);
  border-color: var(--accent);
  border-style: solid;
  transform: scale(1.02);
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--accent) 18%, transparent);
}
@keyframes syncSlotSuccess {
  0%, 100% { background: color-mix(in srgb, var(--accent) 6%, transparent); }
  50%      { background: color-mix(in srgb, var(--success, #22c55e) 35%, transparent);
             box-shadow: 0 0 0 4px color-mix(in srgb, var(--success, #22c55e) 25%, transparent); }
}
.sync-slot-success {
  animation: syncSlotSuccess 0.7s ease;
  border-color: var(--success, #22c55e);
}

/* ── Actions ── */
.sync-actions {
  display: flex; gap: 8px; flex-wrap: wrap;
  margin: 14px 0 10px;
}

/* ── Feedback ── */
.sync-feedback { margin-top: 8px; }
.sync-result {
  display: flex; gap: 12px; align-items: flex-start;
  padding: 14px 16px;
  border-radius: var(--radius);
  border: 1px solid var(--border);
  background: var(--bg-elev);
  animation: syncOpPop 0.32s cubic-bezier(.34,1.56,.64,1);
}
.sync-result-icon { font-size: 26px; line-height: 1; }
.sync-result-title { font-weight: 700; font-size: 14px; margin-bottom: 4px; }
.sync-result-sub   { font-size: 13px; color: var(--text-muted); }
.sync-result-list  { margin: 4px 0 0 18px; padding: 0; font-size: 13px; line-height: 1.55; }
.sync-result-list li { margin: 2px 0; }
.sync-result-ok {
  background: linear-gradient(135deg,
    color-mix(in srgb, var(--success, #22c55e) 18%, var(--bg-elev)),
    var(--bg-elev));
  border-color: color-mix(in srgb, var(--success, #22c55e) 50%, transparent);
}
.sync-result-bad {
  background: linear-gradient(135deg,
    color-mix(in srgb, var(--danger, #ef4444) 14%, var(--bg-elev)),
    var(--bg-elev));
  border-color: color-mix(in srgb, var(--danger, #ef4444) 45%, transparent);
}
.sync-result-info {
  background: linear-gradient(135deg,
    color-mix(in srgb, var(--accent) 12%, var(--bg-elev)),
    var(--bg-elev));
  border-color: color-mix(in srgb, var(--accent) 45%, transparent);
}

/* === Mobile === */
@media (max-width: 768px) {
  .sync-toolbar { padding: 10px 12px; gap: 8px; }
  .sync-bank-wrap { grid-template-columns: 1fr; }
  .sync-trash { flex-direction: row; min-width: 0; }
  .sync-trash-icon { font-size: 20px; }
  .sync-bank-grid { grid-template-columns: repeat(auto-fill, minmax(150px, 1fr)); gap: 8px; }
  .sync-bank-pair { padding: 6px 8px; }
  .sync-board { grid-template-columns: 1fr; gap: 10px; }
  .sync-proc { padding: 10px; }
  .sync-block { padding: 8px 10px; font-size: 14px; }
  .sync-block-letter { font-size: 16px; }
  .sync-op { font-size: 11.5px; padding: 4px 8px; }
  .sync-slot { min-height: 32px; padding: 3px 6px; }
}
@media (max-width: 380px) {
  .sync-pill { font-size: 11px; padding: 4px 8px; }
  .sync-op { font-size: 11px; padding: 3px 7px; }
  .sync-block-letter { font-size: 15px; }
}
